import React from 'react';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import {useTranslation} from 'react-i18next';
import {useGetApi} from 'common/hooks/useGetApi';
import api from 'api';
import AsyncContainer from 'components/AsyncContainer';
import {ISshHostKey} from 'types/SshHostKey';
import Panel from 'components/Panel';
import Button from 'components/Button';
import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import {useModal} from 'common/hooks/useModal';
import AddSshHostKeyDialog from './components/AddSshHostKeyDialog';
import {useDeleteApi} from 'common/hooks/useDeleteApi';
import {ConfirmationDialog} from 'components/dialogs/ConfirmationDialog';
import Typography from '@mui/material/Typography';
import {Grid} from '@mui/material';

type SshHostKeysDialogProps = {
   data?: INav;
   onClose: () => void;
};

const SshHostKeysDialog = ({onClose}: SshHostKeysDialogProps) => {
   const {t} = useTranslation();
   const {open: openAddDialog, handleOpen: handleOpenAdd, handleClose: handleCloseAdd} = useModal();
   const {
      open: openDeleteDialog,
      handleOpen: handleOpenDelete,
      handleClose: handleCloseDelete,
   } = useModal();
   const [selectedRowIndex, setSelectedRowIndex] = React.useState<number>();
   const {request, state, loading} = useGetApi<ISshHostKey[]>(api.sshHostkeys);
   React.useEffect(() => request(), [request]);
   const {request: deleteHostKey} = useDeleteApi(api.sshHostkeys);
   const data = state
      ? state
           .sort((a, b) => a.hostName.localeCompare(b.hostName))
           .reduce(
              (rows: Array<Pick<ISshHostKey, 'id' | 'hostName'> & {fingerprint: string}>, key) => {
                 const {hostName, fingerprints, id} = key;
                 fingerprints.forEach((fingerprint) => rows.push({id, hostName, fingerprint}));
                 return rows;
              },
              []
           )
      : [];
   const handleDelete = () => {
      if (selectedRowIndex !== undefined) {
         const key = data[selectedRowIndex];
         deleteHostKey({
            urlParams: `/${key.id}`,
            onSuccess: () => {
               setSelectedRowIndex(undefined);
               handleCloseDelete();
               request();
            },
         });
      }
   };
   return (
      <Dialog maxWidth="sm" header={DialogTypes.SshHostKeysDialog.dialogName} close={onClose}>
         <AsyncContainer loading={loading}>
            <Panel>
               {state ? (
                  <TableContainer>
                     <Table size={'small'}>
                        <TableHead>
                           <TableRow>
                              <TableCell>{t('Host Address')}</TableCell>
                              <TableCell>{t('Fingerprint')}</TableCell>
                           </TableRow>
                        </TableHead>
                        <TableBody>
                           {data.map(({id, hostName, fingerprint}, rowIndex) => (
                              <TableRow
                                 hover
                                 onClick={(event) => setSelectedRowIndex(rowIndex)}
                                 tabIndex={-1}
                                 key={rowIndex}
                                 selected={rowIndex === selectedRowIndex}
                              >
                                 <TableCell height={32}>{hostName}</TableCell>
                                 <TableCell height={32}>{fingerprint}</TableCell>
                              </TableRow>
                           ))}
                        </TableBody>
                     </Table>
                  </TableContainer>
               ) : null}
            </Panel>
            <Grid container justifyContent="center">
               <Grid item>
                  <Button style={{marginRight: '8px'}} onClick={handleOpenAdd}>
                     {t('Add')}
                  </Button>
               </Grid>
               <Grid item>
                  <Button disabled={selectedRowIndex === undefined} onClick={handleOpenDelete}>
                     {t('Delete')}
                  </Button>
               </Grid>
            </Grid>
            <AddSshHostKeyDialog open={openAddDialog} onAdd={request} onCancel={handleCloseAdd} />
            <ConfirmationDialog
               open={openDeleteDialog}
               title={t('Remove Fingerprint')}
               content={
                  selectedRowIndex !== undefined ? (
                     <Typography>
                        {t(
                           'Do you want to remove the fingerprint {{hostName}} from host {{fingerprint}}?',
                           {
                              hostName: data[selectedRowIndex].hostName,
                              fingerprint: data[selectedRowIndex].fingerprint,
                           }
                        )}
                     </Typography>
                  ) : null
               }
               confirmLabel={t('Yes')}
               cancelLabel={t('No')}
               onOk={handleDelete}
               onCancel={handleCloseDelete}
            />
            <Box textAlign={'center'} marginTop="2rem">
               <Button onClick={onClose}>{t('Close')}</Button>
            </Box>
         </AsyncContainer>
      </Dialog>
   );
};

export default SshHostKeysDialog;
